import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Mail, Lock, User, Phone, Store } from "lucide-react";
import { useAuth } from "@/App";

function formatErr(detail) {
  if (!detail) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail?.msg || detail);
}

export default function AuthDialog({ mode, setMode, onClose, onSuccess }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    user_type: "petshop",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      let u;
      if (mode === "register") {
        u = await register(form);
      } else {
        u = await login(form.email, form.password);
      }
      onSuccess?.();
      if (u?.role === "admin") {
        setTimeout(() => navigate("/admin"), 100);
      }
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="auth-dialog"
    >
      <div
        className="pm-card pm-glow-ring w-full max-w-md p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={onClose} data-testid="auth-dialog-close">
          <X size={20} />
        </button>
        <div className="pm-label mb-2">// {mode === "register" ? "Cadastro" : "Login"}</div>
        <h3 className="font-display text-2xl font-black">
          {mode === "register" ? "Crie sua conta" : "Bem-vindo de volta"}
        </h3>
        <p className="text-white/60 text-sm mt-2">
          {mode === "register"
            ? "Reserve unidades e receba atualizações em primeira mão."
            : "Acesse suas reservas e informações."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          {mode === "register" && (
            <IconInput
              icon={User}
              placeholder="Seu nome"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
              minLength={2}
              testid="auth-name-input"
            />
          )}
          <IconInput
            icon={Mail}
            type="email"
            placeholder="E-mail"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            required
            testid="auth-email-input"
          />
          <IconInput
            icon={Lock}
            type="password"
            placeholder="Senha (mínimo 6 caracteres)"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            required
            minLength={6}
            testid="auth-password-input"
          />
          {mode === "register" && (
            <>
              <IconInput
                icon={Phone}
                placeholder="WhatsApp com DDD"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                required
                minLength={8}
                testid="auth-phone-input"
              />
              <div className="relative">
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none z-10" size={16} />
                <select
                  className="pm-input pm-input-icon appearance-none"
                  value={form.user_type}
                  onChange={(e) => setForm({ ...form, user_type: e.target.value })}
                  data-testid="auth-usertype-input"
                >
                  <option value="petshop">Pet Shop</option>
                  <option value="banho_tosa">Banho & Tosa</option>
                  <option value="comodato">Interesse em Comodato</option>
                  <option value="tutor">Tutor / Casa própria</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
            </>
          )}

          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="auth-error">
              {error}
            </div>
          )}

          <button
            className="pm-btn-primary w-full text-xs mt-2"
            disabled={loading}
            data-testid="auth-submit-button"
          >
            {loading ? <Loader2 size={14} className="animate-spin inline" /> : (mode === "register" ? "Criar conta" : "Entrar")}
          </button>
        </form>

        <div className="mt-5 text-center text-sm text-white/60">
          {mode === "register" ? (
            <>
              Já tem conta?{" "}
              <button className="text-[color:var(--pm-neon)] font-semibold" onClick={() => setMode("login")} data-testid="switch-to-login">
                Entrar
              </button>
            </>
          ) : (
            <>
              Ainda não tem conta?{" "}
              <button className="text-[color:var(--pm-neon)] font-semibold" onClick={() => setMode("register")} data-testid="switch-to-register">
                Cadastre-se
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function IconInput({ icon: Icon, testid, onChange, ...rest }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none z-10" size={16} />
      <input
        className="pm-input pm-input-icon"
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
    </div>
  );
}
